import { defaultColor } from '../DataStructures/colors';
import { defaultWallType, isPVCWall, wallTypes } from '../DataStructures/wallTypes';
import { defaultRoofType, isPVCRoof, isTextileRoof, roofTypes } from '../DataStructures/roofTypes';
import { defaultRoofWindowType, isRidgeLightBand, noRoofWindowType } from '../DataStructures/roofWindowTypes';
import { isXSide } from '../Selectors/Side';
import { canWallSegmentGetObjectType } from '../Rules/canWallSegmentGetObjectType';
import { isRangeObject } from '../Selectors/RangeObject';
import getItemRules from '../Rules/getItemRules';
import createBuilding from './buildingFactory';
import { filterObjectsInRange, fixBuildingDataWithRules, fixValueWithFixedValueRule } from './ruleHelpers';

const initialState = {
  width: 15,
  length: 15,
  height: 4.2,
  xWallSegmentWidth: 5,
  zWallSegmentWidth: 5,
  wallType: defaultWallType,
  roofType: defaultRoofType,
  rainGutter: false,
  roofWindowType: defaultRoofWindowType,
  isRoofWindowEnabled: true,
  isRidgeLightBandEnabled: false,
  wallColor: defaultColor,
  consoleColor: defaultColor,
  roofColor: defaultColor,
  doorColor: defaultColor,
  gateColor: defaultColor,
  windowColor: defaultColor,
  objects: [],
  highlightColor: 0x385dad,
};

export function createWarehouse(inputState = {}) {
  const building = createBuilding(inputState, { calculateDerivedData, initialState });

  const { createSetter } = building;

  const setWallType = createSetter('wallType');
  const setRoofType = createSetter('roofType');
  const setWallColor = createSetter('wallColor');
  const setConsoleColor = createSetter('consoleColor');
  const setRoofColor = createSetter('roofColor');
  const setRainGutter = createSetter('rainGutter');
  const setRoofWindowType = createSetter('roofWindowType');
  const setDoorColor = createSetter('doorColor');
  const setGateColor = createSetter('gateColor');

  return {
    ...building,

    setWallType,
    setRoofType,
    setWallColor,
    setConsoleColor,
    setRoofColor,
    setRainGutter,
    setRoofWindowType,
    setDoorColor,
    setGateColor,
  };
}

function fixWarehouseDataWithRules(state, rules) {
  fixBuildingDataWithRules(state, rules);
  state.rainGutter = fixValueWithFixedValueRule(state.rainGutter, rules.rainGutter);
}

function fixNoRoofWindow(state) {
  if (state.roofWindowType === 'no_roof_window') {
    state.roofWindowType = noRoofWindowType;
  }
}

function removeColors(state) {
  Object.assign(state, {
    wallColor: null,
    consoleColor: null,
    roofColor: null,
    doorColor: null,
    gateColor: null,
    windowColor: null,
  });
}

function fixElementType(state, target, availableItems, defaultValue) {
  if (!availableItems.some(({ variant }) => variant === state[target]?.variant)) {
    state[target] = defaultValue;
  }
}

function calculateDerivedData(state) {
  const rules = getItemRules(state);
  rules && fixWarehouseDataWithRules(state, rules);
  fixNoRoofWindow(state);
  removeColors(state);
  fixElementType(state, 'wallType', wallTypes, defaultWallType);
  fixElementType(state, 'roofType', roofTypes, defaultRoofType);

  let xWallSegmentWidth = 5;

  let length = state.length;
  length = length - (length % xWallSegmentWidth);

  const isRoofWindowEnabled = !isTextileRoof(state.roofType) && !isPVCRoof(state.roofType);
  const isRidgeLightBandEnabled = isRoofWindowEnabled && length >= 30;

  const removeRoofWindow =
    !isRoofWindowEnabled || (!isRidgeLightBandEnabled && isRidgeLightBand(state.roofWindowType));
  const roofWindowType = removeRoofWindow ? noRoofWindowType : state.roofWindowType;

  const isRoofColorEnabled = !isTextileRoof(state.roofType) && !isPVCRoof(state.roofType);

  const isWallColorEnabled = !isPVCWall(state.wallType);

  const xWallSegmentCount = length / xWallSegmentWidth;
  const zWallSegmentCount = state.width / state.zWallSegmentWidth;

  Object.assign(state, {
    xWallSegmentWidth,
    xWallSegmentCount,
    zWallSegmentCount,
    length,
    roofWindowType,
    isRoofWindowEnabled,
    isRidgeLightBandEnabled,
    isRoofColorEnabled,
    isWallColorEnabled,
    windowColor: state.consoleColor,
  });

  const objects = filterObjectsInRange(state);

  // re-add the objects with validation
  state.objects = [];
  objects.forEach((object) => {
    const { type, side, index, height } = object;
    if (isRangeObject(type)) {
      const maxIndex = isXSide(side) ? xWallSegmentCount - 1 : zWallSegmentCount - 1;
      // skip out-of-range objects
      if (object.indexFrom > maxIndex) {
        return;
      }
      // shrink to fit
      if (object.indexTo > maxIndex) {
        object.indexTo = maxIndex;
      }
    }
    if (canWallSegmentGetObjectType(state, type, side, index, height)) {
      state.objects.push(object);
    }
  });

  return state;
}

export default createWarehouse;
